import { useEffect, useMemo, useState } from 'react';

import { getLocalStorageItem, setLocalStorageItem } from '@/utils/localStorage';

import type { SectionScoreColumn } from '../sectionScores';

const storageKey = (organizationId: string) => `compass_qc_section_columns_${organizationId}`;

const isSectionScoreColumn = (value: unknown): value is SectionScoreColumn =>
  typeof (value as SectionScoreColumn)?.sectionId === 'string' &&
  typeof (value as SectionScoreColumn)?.header === 'string';

const readStoredColumns = (organizationId: string): SectionScoreColumn[] => {
  const stored = getLocalStorageItem<unknown>(storageKey(organizationId));
  return Array.isArray(stored) ? stored.filter(isSectionScoreColumn) : [];
};

// Section columns are derived from the page's rows and their scorecards, which are empty or
// partial while a filter change or reload is loading. Deriving them live made the columns vanish
// and come back (in a different order while scorecards arrived one by one), shifting the table.
// While `settling`, keep showing the last settled columns; they are remembered per org so a
// reload starts with them too. Stores section ids and titles only (no PHI).
export const useStickySectionScoreColumns = ({
  organizationId,
  enabled,
  columns,
  settling,
}: {
  organizationId: string | null | undefined;
  enabled: boolean;
  columns: SectionScoreColumn[];
  settling: boolean;
}): SectionScoreColumn[] => {
  const stored = useMemo(
    () => (organizationId ? readStoredColumns(organizationId) : []),
    [organizationId],
  );
  const [settled, setSettled] = useState<{
    organizationId: string;
    columns: SectionScoreColumn[];
  }>();

  useEffect(() => {
    if (!enabled || settling || !organizationId) return;
    setSettled({ organizationId, columns });
    setLocalStorageItem(storageKey(organizationId), columns);
  }, [enabled, settling, organizationId, columns]);

  if (!enabled) return [];
  if (!settling) return columns;
  return settled && settled.organizationId === organizationId ? settled.columns : stored;
};
